import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { PlatformMessageTemplate } from "../../../cores/platformAutomations/services/api";
import {
  createDelayStep,
  createEmailStep,
  isFirstSendEmailStep,
  moveStep,
  reindexSteps,
} from "../../../cores/platformAutomations/utils/draftDefinition.utils";
import {
  buildDelay,
  formatDelayLabel,
  getDelayParts,
  type DelayUnit,
} from "../../../cores/platformAutomations/utils/delay.utils";
import {
  PLATFORM_LOCALES,
  PlatformAutomationDraft,
  PlatformAutomationStep,
  SendEmailStep,
} from "../../../cores/platformAutomations/types";

type Props = {
  draft: PlatformAutomationDraft;
  templates: PlatformMessageTemplate[];
  onChange: (draft: PlatformAutomationDraft) => void;
  onTestStep?: (stepId: string) => void;
};

const stepSummary = (
  step: PlatformAutomationStep,
  templates: PlatformMessageTemplate[]
) => {
  if (step.type === "delay") {
    return `Esperar ${formatDelayLabel(step.delay)}`;
  }
  const email = step as SendEmailStep;
  const locale = email.defaultLocale || "es";
  const ref = email.templatesByLocale?.[locale];
  const template = templates.find((t) => t.id === ref?.templateId);
  return template?.draftDefinition?.subject || template?.name || "Elegí un template";
};

export default function PlatformAutomationStepsEditor({
  draft,
  templates,
  onChange,
  onTestStep,
}: Props) {
  const steps = draft.steps || [];

  const updateSteps = (nextSteps: PlatformAutomationStep[]) =>
    onChange({ ...draft, steps: reindexSteps(nextSteps) });

  const updateStep = (index: number, patch: Partial<PlatformAutomationStep>) => {
    const next = steps.map((step, i) =>
      i === index ? ({ ...step, ...patch } as PlatformAutomationStep) : step
    );
    updateSteps(next);
  };

  const removeStep = (index: number) => {
    updateSteps(steps.filter((_, i) => i !== index));
  };

  const addDelay = () => updateSteps([...steps, createDelayStep(5, "minutes")]);
  const addEmail = () =>
    updateSteps([...steps, createEmailStep(draft.defaultLocale || "es")]);

  const setTemplateForLocale = (
    stepIndex: number,
    locale: string,
    templateId: number | null
  ) => {
    const step = steps[stepIndex] as SendEmailStep;
    const templatesByLocale = { ...(step.templatesByLocale || {}) };
    if (!templateId) {
      delete templatesByLocale[locale];
    } else {
      const template = templates.find((t) => t.id === templateId);
      templatesByLocale[locale] = {
        templateSource: "wembii",
        templateId,
        templateVersionId: template?.publishedVersionId,
      };
    }
    updateStep(stepIndex, { templatesByLocale } as Partial<SendEmailStep>);
  };

  return (
    <div className="pa-steps">
      <div className="pa-steps__header">
        <div>
          <h5 className="pa-steps__title">Secuencia de pasos</h5>
          <p className="pa-steps__subtitle">
            Armá el journey con esperas y emails. Se ejecuta en orden de arriba
            hacia abajo.
          </p>
        </div>
        <div className="pa-steps__add">
          <button type="button" className="email-mkt__ghost-btn" onClick={addDelay}>
            <FontAwesomeIcon icon={["fas", "clock"]} /> Espera
          </button>
          <button type="button" className="email-mkt__primary-btn" onClick={addEmail}>
            <FontAwesomeIcon icon={["fas", "envelope"]} /> Email
          </button>
        </div>
      </div>

      {steps.length === 0 ? (
        <div className="pa-steps__empty">
          <FontAwesomeIcon icon={["fas", "route"]} />
          <p>Todavía no hay pasos. Agregá una espera o un email para empezar.</p>
        </div>
      ) : (
        <div className="pa-steps__list">
          {steps.map((step, index) => (
            <div key={step.id} className="pa-step-card">
              <div className="pa-step-card__rail">
                <span className="pa-step-card__dot" />
                {index < steps.length - 1 && <span className="pa-step-card__line" />}
              </div>

              <div className="pa-step-card__body">
                <div className="pa-step-card__head">
                  <span
                    className={`pa-step-card__badge pa-step-card__badge--${step.type}`}
                  >
                    {step.type === "delay" ? "Espera" : "Email"}
                  </span>
                  <strong>{stepSummary(step, templates)}</strong>
                  <div className="pa-step-card__actions">
                    <button
                      type="button"
                      title="Subir"
                      disabled={index === 0}
                      onClick={() => updateSteps(moveStep(steps, index, index - 1))}
                    >
                      <FontAwesomeIcon icon={["fas", "arrow-up"]} />
                    </button>
                    <button
                      type="button"
                      title="Bajar"
                      disabled={index === steps.length - 1}
                      onClick={() => updateSteps(moveStep(steps, index, index + 1))}
                    >
                      <FontAwesomeIcon icon={["fas", "arrow-down"]} />
                    </button>
                    <button
                      type="button"
                      title="Eliminar"
                      className="is-danger"
                      onClick={() => removeStep(index)}
                    >
                      <FontAwesomeIcon icon={["fas", "trash"]} />
                    </button>
                    {step.type === "send_email" && onTestStep && (
                      <button
                        type="button"
                        title="Probar email"
                        className="is-test"
                        onClick={() => onTestStep(step.id)}
                      >
                        <FontAwesomeIcon icon={["fas", "paper-plane"]} />
                      </button>
                    )}
                  </div>
                </div>

                {step.type === "delay" ? (
                  <div className="pa-step-card__row pa-step-card__delay-row">
                    <label className="pa-field pa-field--inline">
                      <span>Cantidad</span>
                      <input
                        type="number"
                        min={1}
                        max={3650}
                        value={getDelayParts(step.delay).amount}
                        onChange={(e) => {
                          const { unit } = getDelayParts(step.delay);
                          updateStep(index, {
                            delay: buildDelay(Number(e.target.value) || 1, unit),
                          });
                        }}
                      />
                    </label>
                    <label className="pa-field pa-field--inline">
                      <span>Unidad</span>
                      <select
                        value={getDelayParts(step.delay).unit}
                        onChange={(e) => {
                          const { amount } = getDelayParts(step.delay);
                          updateStep(index, {
                            delay: buildDelay(amount, e.target.value as DelayUnit),
                          });
                        }}
                      >
                        <option value="minutes">Minutos</option>
                        <option value="hours">Horas</option>
                        <option value="days">Días</option>
                      </select>
                    </label>
                  </div>
                ) : (
                  <div className="pa-step-card__email-fields">
                    {isFirstSendEmailStep(steps, index) ? (
                      <div className="pa-step-card__row">
                        <label className="pa-field">
                          <span>Envío</span>
                          <select
                            value={
                              (step as SendEmailStep).deliveryTiming ||
                              "immediate"
                            }
                            onChange={(e) =>
                              updateStep(index, {
                                deliveryTiming: e.target.value as
                                  | "immediate"
                                  | "next_scheduled_time",
                              })
                            }
                          >
                            <option value="immediate">
                              Inmediato al activarse
                            </option>
                            <option value="next_scheduled_time">
                              Inicio programado a las…
                            </option>
                          </select>
                        </label>
                        {(step as SendEmailStep).deliveryTiming ===
                          "next_scheduled_time" && (
                          <label className="pa-field">
                            <span>Hora (timezone del usuario)</span>
                            <input
                              type="time"
                              value={
                                (step as SendEmailStep).sendAtTime || "10:00"
                              }
                              onChange={(e) =>
                                updateStep(index, {
                                  sendAtTime: e.target.value,
                                })
                              }
                            />
                          </label>
                        )}
                      </div>
                    ) : (
                      <p className="pa-step-card__hint">
                        Se envía cuando vence la espera anterior (minutos, horas
                        o días).
                      </p>
                    )}

                    <div className="pa-step-card__templates">
                      <span className="pa-step-card__templates-label">
                        Template por idioma
                      </span>
                      {PLATFORM_LOCALES.map((locale) => {
                        const ref = (step as SendEmailStep).templatesByLocale?.[
                          locale
                        ];
                        const selected = templates.find(
                          (t) => t.id === ref?.templateId
                        );
                        return (
                          <label key={locale} className="pa-field pa-field--template">
                            <span>{locale}</span>
                            <select
                              value={ref?.templateId || ""}
                              onChange={(e) =>
                                setTemplateForLocale(
                                  index,
                                  locale,
                                  e.target.value ? Number(e.target.value) : null
                                )
                              }
                            >
                              <option value="">— Elegir template —</option>
                              {templates
                                .filter((t) => t.locale === locale || t.locale === "es")
                                .map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                    {t.status !== "published" ? " (borrador)" : ""}
                                  </option>
                                ))}
                            </select>
                            {selected && selected.status !== "published" && (
                              <small className="pa-step-card__warn">
                                Publicá el template antes de publicar el flujo
                              </small>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
